import { Search, Timer } from '../icons';
import { useState, useMemo, useEffect, useRef } from 'react';
import AppNavigationMenu from '../appNavigationMenu/appNavigationMenu';

import { LOCAL_STORAGE_NAMES, ROUTES, UserType } from '../../constants/constants';
import { useLocation } from 'react-router-dom';
import { PageContext } from '../../context/PageContext';
import { IconButton, Tooltip } from '../Ant';
import { LayoutWrapper } from '../../assets/styles/layout/mainLayout.styled';
import { Badge, Flex } from 'antd';
import { useTranslation } from 'react-i18next';
import NotificationBellButton from '../notificationBellButton/notificationBellButton';
import { FeatureSelectionTab } from '../../interface/featureSelection';
import { useFeatureContext } from '../../context/FeatureContext';
import { useFetchUserParameters } from '../../hooks/useFetchUserParameters';
import { useTransactionParameters } from '../../context/TransactionParameterContext';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { useFetchUser } from '../../hooks/useFetchUser';
import { FeatureModalProvider } from '../../context/FeatureModalContext';
import { useAnalytics } from '../../hooks/useAnalytics';
import { AnalyticsEvent, FeatureButtons } from '../../interface/analytics';
import AppUpdateChecker from '../appUpdate/appUpdateChecker';
import { useBadgeCounts } from '../../hooks/useBadgeCounts';
import { useUser } from '../../context/UserContext';
import { useFocusOnNavigation } from '../../hooks/useFocusOnNavigation';
import { usePaneEntryFocus } from '../../hooks/usePaneEntryFocus';
import { tabOnFromUntabbable } from '../../helpers/tabFromUntabbableHelper';
interface LayoutProps {
  children: React.ReactNode;
  title: React.ReactNode;
}

const ONBOARDING_SCREEN_ROUTES: string[] = [
  ROUTES.DEFAULT,
  ROUTES.FEATURE_SELECTION,
  ROUTES.ACCESS_FORBIDDEN,
  ROUTES.NO_INTERNET,
  ROUTES.ERROR,
  ROUTES.ANALYTICS,
];

const MainLayout = ({ children, title: initialTitle }: LayoutProps) => {
  const { t } = useTranslation();
  const { push, clear, takeReturnFocusRequest } = useNavigationStack();
  const { trackAction } = useAnalytics();
  const { updateUser, updateUserDetails } = useUser();
  const user = localStorage.getItem(LOCAL_STORAGE_NAMES.user) ?? null;
  const [title, setTitle] = useState(initialTitle);
  const { features } = useFeatureContext();
  const [actions, setActions] = useState<React.ReactNode>(null);
  const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false); // <-- Parent manages the state
  const { notificationBadgeCount, timerBadgeCount } = useBadgeCounts();
  const location = useLocation();
  const layoutRef = useRef<HTMLDivElement>(null);
  useFocusOnNavigation(layoutRef, location.pathname, takeReturnFocusRequest);
  usePaneEntryFocus(layoutRef);
  const showSettings = [ROUTES.DASHBOARD].includes(location.pathname);
  const showMenu = [ROUTES.DASHBOARD].includes(location.pathname);
  const isOnboardingScreen = ONBOARDING_SCREEN_ROUTES.includes(location.pathname);
  const { updateParameters } = useTransactionParameters();
  const { data: userDetails, isLoading: isLoadingUser } = useFetchUser(
    {
      relations: ['businessUnit', 'userRole', 'userGroups'],
    },
    {
      enabled: user
        ? JSON.parse(user)?.id !== undefined && JSON.parse(user).id !== null
        : undefined,
    },
  );
  const { data: userParameterDetails, refetch } =
    useFetchUserParameters({ parametersToGet: 'transaction', enabled: !!userDetails?.id }) ?? {};

  useEffect(() => {
    setTitle((prev) => {
      // Only reset if nothing was ever set, or we're on a new page with a different route
      if (!prev || prev === '' || typeof prev === 'string') {
        return initialTitle;
      }
      return prev;
    });
  }, [initialTitle, location.pathname]);

  useEffect(() => {
    if (userDetails && !isLoadingUser) {
      updateUserDetails(userDetails);
      updateUser(userDetails);
      refetch();
    }
  }, [userDetails, isLoadingUser]);

  useEffect(() => {
    if (userParameterDetails?.parameters?.transactionParameters) {
      updateParameters(userParameterDetails?.parameters?.transactionParameters);
    }
  }, [userParameterDetails]);

  const isFeatureEnabled = (featureId: string) => !!features[featureId];
  const contextValue = useMemo(() => ({ setTitle, setActions }), [setTitle, setActions]);
  const hasUpdateChecker = location.pathname === ROUTES.DASHBOARD && showUpdateModal;

  return (
    <PageContext.Provider value={contextValue}>
      <FeatureModalProvider>
        {location.pathname === ROUTES.DASHBOARD && (
          <AppUpdateChecker onUpdateAvailable={setShowUpdateModal} />
        )}
        <LayoutWrapper
          ref={layoutRef}
          onKeyDown={tabOnFromUntabbable}
          className={`${hasUpdateChecker ? 'has-update-checker' : ''} ${
            hasUpdateChecker ? 'has-top-banner' : ''
          } ${isOnboardingScreen ? 'onboarding-screen' : ''}`}
        >
          <header className={hasUpdateChecker ? 'has-top-banner main-header' : 'main-header'}>
            {title} {actions && <div className="header-actions">{actions}</div>}
            <Flex align="center" gap={5}>
              {showSettings && (
                <Tooltip title={t('common:timers.timer')} placement="top" arrow={false}>
                  <Badge
                    count={location.pathname === ROUTES.TIMER_LIST ? 0 : timerBadgeCount}
                    offset={[-7, 8]}
                  >
                    <IconButton
                      data-testid="timer-button"
                      aria-label={t('common:timers.timer')}
                      icon={<Timer />}
                      onClick={() => {
                        trackAction(AnalyticsEvent.BUTTON_CLICK, FeatureButtons.TIMER);
                        clear();
                        push(ROUTES.TIMER_LIST);
                      }}
                      size="large"
                      className={location.pathname === ROUTES.TIMER_LIST ? 'page-active' : ''}
                    />
                  </Badge>
                </Tooltip>
              )}

              {showMenu &&
                userDetails?.userType !== UserType.RESTRICTED &&
                isFeatureEnabled(String(FeatureSelectionTab.conflictSearch)) && (
                  <Tooltip title={t('conflict:conflictSearch')} placement="top" arrow={false}>
                    <IconButton
                      data-testid="conflict-search-button"
                      aria-label={t('conflict:conflictSearch')}
                      icon={<Search />}
                      className={location.pathname === ROUTES.CONFLICT_SEARCH ? 'page-active' : ''}
                      onClick={() => {
                        trackAction(AnalyticsEvent.BUTTON_CLICK, FeatureButtons.CONFLICT_SEARCH);
                        clear();
                        push(ROUTES.CONFLICT_SEARCH);
                      }}
                      size="large"
                    />
                  </Tooltip>
                )}
              {showSettings && (
                <NotificationBellButton notificationBadgeCount={notificationBadgeCount} />
              )}
              {showMenu && <AppNavigationMenu />}
            </Flex>
          </header>
          <div className="main-container">{children}</div>
        </LayoutWrapper>
      </FeatureModalProvider>
    </PageContext.Provider>
  );
};

export default MainLayout;
